import carriersConfig from '@/config/carriers/carriersConfig'
import { supportLanguage } from '@/config/i18nConfig'
import { getCurrentRecommendCarriers } from 'config/carriers/carriersConfig'
import Head from 'next/head'
import { useEffect } from 'react'
import { queryCarrierContent } from 'src/api/platform'
import CarriersInfoHeader from 'src/components/carriersPage/carriersInfoHeader'
import CarrierContent from 'src/components/carriersPage/Content'
import JoinBanner from 'src/components/carriersPage/joinBanner'
import RecommendList from 'src/components/carriersPage/RecommendList'
import Layout from 'src/components/layout'
import LinkSeo from 'src/components/layout/linkSeo'
import MetaSeo from 'src/components/layout/metaSeo'
import useCarrierMeta from 'src/hooks/business/useCarrierMeta'
import useGA from 'src/hooks/use_ga'
import useI18n from 'src/hooks/use_i18n'
import { AllCarriersInfoDTO, CarrierInfoMarkDTO, CmsCarrier } from 'src/types'
import { carriersData, filterCarriersName, findKeyByValue, numberFormat } from 'src/utils/carriers_info'
import { getV5Front } from 'src/utils/server_global'
import { DEFAULT_CARRIER_INFO_MARK } from '../../constants/carrier_info_mark'

const logger = require('@/config/logger')

interface PageProps {
  carriersDataType: string // 定义页面性质
  currentPageJson: AllCarriersInfoDTO[]
  currentPage: number
  totalCarriersStr: number | string
  totalCarriersNum: number
  carrierInfoMark: CarrierInfoMarkDTO
  carriersInfoData: any
  carrier: AllCarriersInfoDTO
  country: string
  slug: string
  realLocal: string
  recommendList: (AllCarriersInfoDTO & { type: string; icon: string })[]
  cmsCarrier: CmsCarrier
}

export default function CarriersInfo({
  carriersDataType,
  carrierInfoMark,
  totalCarriersStr,
  carriersInfoData,
  carrier,
  country,
  slug,
  realLocal,
  cmsCarrier,
  recommendList
}: PageProps) {
  useGA('carriers-index')
  const { activeLocale: locale, i18nFormatAll } = useI18n()
  // 获取对应运输商不同语言的名称
  const carrierName = carrier[`_name_${locale}`] || carrier._name
  const metaTDK = useCarrierMeta({ carriersInfoData, cmsCarrier, carrierName })
  useEffect(() => {
    const AD = require('@/plugins/ad_adapter/carriers.js')
    AD?.() // 广告插入
  }, [])

  const subTitle = i18nFormatAll(cmsCarrier?.subtitle || carriersInfoData.desc || cmsCarrier?.template?.subtitle, {
    carriersName: carrierName,
    carriersNum: totalCarriersStr
  })
  return (
    <>
      <MetaSeo {...metaTDK} />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org/",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Homepage",
                "item": "https://www.17track.net/${realLocal}"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "carriers",
                "item": "https://www.17track.net/${realLocal}/carriers"
              },{
                "@type": "ListItem",
                "position": 3,
                "name": "${encodeURIComponent(slug)}",
                "item": "https://www.17track.net/${realLocal}/carriers/${encodeURIComponent(slug)}"
              }]
            }
          `
          }}
        />
      </Head>
      <LinkSeo TagType="carriers_info" TagTypeValue={filterCarriersName(carrier._name)} routeLang={realLocal} />
      <Layout showAllPlatform carrierInfoMark={carrierInfoMark} country={country}>
        <CarriersInfoHeader
          carrier={carrier}
          h1={cmsCarrier?.head_title || carriersInfoData?.headH1}
          subTitle={subTitle}
          carrierName={carrierName}
          country={country}
        />
        <CarrierContent
          totalCarriersStr={totalCarriersStr}
          carriersInfoData={carriersInfoData}
          carrier={carrier}
          realLocal={realLocal}
          cmsCarrier={cmsCarrier}
          country={country}
          carrierName={carrierName}
          carrierInfoMark={carrierInfoMark}
          carriersDataType={carriersDataType}
        />

        <RecommendList recommendList={recommendList} />
        <JoinBanner />
      </Layout>
    </>
  )
}

export async function getServerSideProps({ locale, req, params }) {
  try {
    const supportLocale = supportLanguage.includes(locale) ? locale : 'en'
    // 目前只处理一级路由
    const slug = params?.slug || 'index'
    // 绕过是走页数url即page, 运输商等二级文件路由走name, 首页index
    const { 'cf-ipcountry': country } = req.headers
    logger.info(`carriers-${slug}请求header：`, req.headers)

    const [V5Front, carrierInfoMark, allCarriersJson] = await Promise.all([
      getV5Front(['carriers'], supportLocale),
      fetch(`https://res.17track.net/asset/carrier/info/indicator.${supportLocale}.json`)
        .then((res) => res.json())
        .catch((error) => {
          logger.error(`carriers-${slug}页面获取carrierInfoMark信息异常`, { message: error.message })
          return DEFAULT_CARRIER_INFO_MARK // 兜底数据
        }),
      fetch(`https://res.17track.net/asset/carrier/info/carrier.all.json`)
        .then((res) => res.json())
        .catch((error) => {
          logger.error(`carriers-${slug}页面获取allCarriersJson数据异常`, { message: error.message })
          return [] // 兜底无数据
        })
    ])

    // 底部运输商推荐模块 运输商列表
    const recommendList = getCurrentRecommendCarriers(country, supportLocale).map((key) => {
      if (typeof key === 'number') {
        return allCarriersJson.find((item) => item.key === key)
      }
      return key
    })

    const carrier = allCarriersJson?.find((item: any) => {
      // 规范化 filterCarriersName处理 name 和 key
      return filterCarriersName(item._name) === filterCarriersName(slug) || item.key === Number(slug)
    })

    if (!carrier) {
      return {
        redirect: {
          destination: `/${supportLocale}/carriers`,
          permanent: false // or true, based on your requirement
        }
      }
    }

    if (/^\d+$/.test(slug)) {
      // 将如https://www.17track.net/en/carriers/1021  重定向为 https://www.17track.net/en/carriers/afghan-post
      return {
        redirect: {
          destination: `/${locale}/carriers/${filterCarriersName(carrier._name)}`,
          permanent: false
        }
      }
    }

    const carrierRes = await queryCarrierContent({ carrier_key: carrier?.key, language: locale }).catch((error) => {
      logger.error(`carriers-${slug}详情页面获取carrier数据异常`, {
        message: error.message,
        params: { carrier_key: carrier?.key, language: locale }
      })
      return null // 兜底无数据
    })

    logger.info(`carrier-${slug}获取详情信息`, {
      data: carrierRes,
      params: { carrier_key: carrier?.key, language: locale }
    })
    // 详情页根据seo 运输商文案关系表去得到获取seo文案的type值
    const carriersSeoKey = findKeyByValue(carriersConfig.carriersSeo, carrier?.key, 'seo')

    return {
      props: {
        locale: supportLocale,
        realLocal: locale,
        V5Front,
        carrierInfoMark,
        totalCarriersStr: numberFormat(allCarriersJson.length),
        carriersInfoData: carriersData({ _key: carrier?.key, _lang: locale, _type: carriersSeoKey || 'seo' }) || {},
        carrier,
        recommendList,
        cmsCarrier: carrierRes?.data || {},
        carriersDataType: carriersSeoKey || 'null',
        country: country ?? 'CN',
        slug
      }
    }
  } catch (error: any) {
    logger.error('carriers详情页面请求异常', { message: error.message })
  }
}
